import React from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Image,
  Pressable,
} from "react-native";
import { AppText } from "@/components/AppText";
import { FontFamilies } from "@/constants/Typography";
import { Offer } from "@/types/offer";
import Icons from "@/assets/icons";
import { calculatePayoutAmount } from "@/utils/payout";

type Props = {
  visible: boolean;
  offer: Offer;
  onClose: () => void;
  onContinue: () => void;
};

const OfferDetailsPopup: React.FC<Props> = ({
  visible,
  offer,
  onClose,
  onContinue,
}: Props) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable
          style={styles.container}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <View style={styles.closeIcon}>
              <View style={styles.closeBar1} />
              <View style={styles.closeBar2} />
            </View>
          </TouchableOpacity>

          {/* Content */}
          <View style={styles.content}>
            {/* Header with Logo */}
            <View style={styles.header}>
              {/* Logo */}
              <View style={styles.logoContainer}>
                <View style={styles.whiteBackground} />
                {offer.image ? (
                  <Image
                    source={{ uri: offer.image }}
                    style={styles.logo}
                    resizeMode="contain"
                  />
                ) : (
                  <View style={styles.placeholderLogo}>
                    <AppText style={styles.placeholderText}>
                      {offer.name.charAt(0).toUpperCase()}
                    </AppText>
                  </View>
                )}
              </View>

              {/* Title */}
              <View style={styles.titleContainer}>
                <AppText style={styles.title}>
                  ${calculatePayoutAmount(offer.payout)}{" "}
                  {offer.name} signup offer
                </AppText>
              </View>

              {/* Description */}
              <View style={styles.descriptionContainer}>
                <AppText style={styles.descriptionText}>
                  {offer.description}
                </AppText>
              </View>

              {/* Goal Checkboxes */}
              {offer.goals && offer.goals.length > 0 && (
                <View style={styles.goalsContainer}>
                  {offer.goals.map((goal) => (
                    <View key={goal} style={styles.goalItem}>
                      <Icons.OfferCheck width={20} height={20} />
                      <AppText style={styles.goalText}>{goal}</AppText>
                    </View>
                  ))}
                </View>
              )}
            </View>

            {/* Continue Button */}
            <TouchableOpacity
              style={styles.continueButton}
              onPress={onContinue}
            >
              <AppText style={styles.continueButtonText}>Continue</AppText>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    cursor: "default" as any,
  },
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    width: 355,
    maxWidth: "90%",
    paddingBottom: 24,
    cursor: "default" as any,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
    cursor: "pointer" as any,
  },
  closeIcon: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  closeBar1: {
    position: "absolute",
    width: 16,
    height: 2,
    backgroundColor: "#969696",
    transform: [{ rotate: "45deg" }],
  },
  closeBar2: {
    position: "absolute",
    width: 16,
    height: 2,
    backgroundColor: "#969696",
    transform: [{ rotate: "-45deg" }],
  },
  content: {
    paddingTop: 56,
    paddingHorizontal: 16,
    gap: 16,
  },
  header: {
    alignItems: "center",
    gap: 16,
  },
  logoContainer: {
    width: 80,
    height: 80,
    borderRadius: 16,
    backgroundColor: "#F3F3F3",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    position: "relative",
  },
  whiteBackground: {
    position: "absolute",
    width: 80,
    height: 80,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
  },
  logo: {
    width: 80,
    height: 80,
    position: "absolute",
    alignSelf: "center",
  },
  placeholderLogo: {
    width: "100%",
    height: "100%",
    backgroundColor: "#F3F3F3",
    justifyContent: "center",
    alignItems: "center",
  },
  placeholderText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 24,
    color: "#6B7280",
  },
  titleContainer: {
    alignItems: "center",
    gap: 0,
  },
  title: {
    fontFamily: FontFamilies.InterTight.Medium,
    fontSize: 24,
    lineHeight: 30,
    color: "#000000",
    textAlign: "center",
  },
  descriptionContainer: {
    width: "100%",
    marginTop: 8,
  },
  descriptionText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 22,
    color: "#374151",
    textAlign: "center",
  },
  continueButton: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingVertical: 16.5,
    paddingHorizontal: 20,
    alignItems: "center",
    marginTop: 12,
    cursor: "pointer" as any,
  },
  continueButtonText: {
    fontFamily: FontFamilies.Inter.Bold,
    fontSize: 17,
    lineHeight: 23,
    color: "#FFFFFF",
  },
  goalsContainer: {
    width: "100%",
    gap: 4,
  },
  goalItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    paddingVertical: 4,
  },
  goalText: {
    flex: 1,
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#000000",
    flexWrap: "wrap",
  },
});

export default OfferDetailsPopup;
